import { ReactNode, useCallback } from 'react';
import { ButtonGroup, Tooltip } from '@mui/material';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import LocationSearchingOutlinedIcon from '@mui/icons-material/LocationSearchingOutlined';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import { TransformControlsMode } from '@fabric-web/3d-viewer';
import { TRANSPARENT_COLOR } from '@/constants';
import { viewerBackground } from '@/theme/colors';
import { OutlinedIconButton } from './styles';
import { ALL_TRANSFORM_MODES } from '../constants';

const ActionButton = ({
  title,
  icon,
  isDisabled,
  isSelected,
  onClick,
  backgroundColor = TRANSPARENT_COLOR,
  selectedBackgroundColor = '#fff',
}: {
  title: string;
  icon: ReactNode;
  isDisabled: boolean;
  isSelected: boolean;
  onClick: () => void;
  backgroundColor?: string | number;
  selectedBackgroundColor?: string | number;
}) => {
  return (
    <Tooltip title={title}>
      <OutlinedIconButton
        sx={{
          background: isSelected ? selectedBackgroundColor : backgroundColor,
        }}
        onClick={onClick}
        disabled={isDisabled}
      >
        {icon}
      </OutlinedIconButton>
    </Tooltip>
  );
};

const TransformModeButton = (props: {
  title: string;
  transformModeToSelect: TransformControlsMode;
  transformMode: TransformControlsMode | undefined;
  setTransformMode: (mode: TransformControlsMode | undefined) => void;
  icon: ReactNode;
  isDisabled: boolean;
  backgroundColor?: string | number;
  selectedBackgroundColor?: string | number;
}) => {
  const { transformMode, setTransformMode, transformModeToSelect } = props;
  const isSelected = transformMode === transformModeToSelect;
  return (
    <ActionButton
      isSelected={isSelected}
      {...props}
      onClick={() => setTransformMode(isSelected ? undefined : transformModeToSelect)}
    />
  );
};

type TransformButtonGroupProps = {
  transformMode: TransformControlsMode | undefined;
  setTransformMode: (transformMode: TransformControlsMode | undefined) => void;
  allowModes?: TransformControlsMode[];
  onSetRotationCenter?: () => void;
  isWaitingForRotationCenter?: boolean;
};

export const TransformButtonGroup = ({
  transformMode,
  setTransformMode,
  allowModes = ALL_TRANSFORM_MODES,
  onSetRotationCenter,
  isWaitingForRotationCenter = false,
}: TransformButtonGroupProps) => {
  const isModeAllowed = useCallback(
    (mode: TransformControlsMode) => allowModes.findIndex((value) => value === mode) !== -1,
    [allowModes]
  );

  const isAllDisabled = isWaitingForRotationCenter;

  return (
    <>
      <ButtonGroup sx={{ paddingBottom: '10px' }}>
        {isModeAllowed(TransformControlsMode.Translate) && (
          <TransformModeButton
            title="Move"
            transformModeToSelect={TransformControlsMode.Translate}
            transformMode={transformMode}
            setTransformMode={setTransformMode}
            icon={
              <OpenWithIcon
                style={{
                  color:
                    transformMode === TransformControlsMode.Translate ? viewerBackground : '#fff',
                }}
              />
            }
            isDisabled={isAllDisabled}
          />
        )}
        {isModeAllowed(TransformControlsMode.Rotate) && (
          <TransformModeButton
            title="Rotate"
            transformModeToSelect={TransformControlsMode.Rotate}
            transformMode={transformMode}
            setTransformMode={setTransformMode}
            icon={
              <FlipCameraAndroidIcon
                style={{
                  color: transformMode === TransformControlsMode.Rotate ? viewerBackground : '#fff',
                }}
              />
            }
            isDisabled={isAllDisabled}
          />
        )}
        {isModeAllowed(TransformControlsMode.Scale) && (
          <TransformModeButton
            title="Symmetric Resize"
            transformModeToSelect={TransformControlsMode.Scale}
            transformMode={transformMode}
            setTransformMode={setTransformMode}
            icon={
              <ViewInArIcon
                style={{
                  color: transformMode === TransformControlsMode.Scale ? viewerBackground : '#fff',
                }}
              />
            }
            isDisabled={isAllDisabled}
          />
        )}
        {onSetRotationCenter && (
          <ActionButton
            title="Set Rotation Center"
            onClick={() => onSetRotationCenter()}
            isSelected={isWaitingForRotationCenter}
            icon={
              <LocationSearchingOutlinedIcon
                style={{
                  color: isWaitingForRotationCenter ? viewerBackground : '#fff',
                }}
              />
            }
            isDisabled={transformMode !== undefined}
          />
        )}
      </ButtonGroup>
    </>
  );
};
